import { useMemo, VFC } from 'react';

import ConnectedTagSelect from 'dashboard/components/ConnectedTagSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { taggableObjectsFor, UsableObjectConfig } from 'dashboard/lib/workflows/objects';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionRemoveTagV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';

interface ParamsRemoveTagV1Props {
  editable: boolean;
  errors: WorkflowError[];
  focusStep: (token: string) => void;
  params: WorkflowActionRemoveTagV1['params'];
  usableObjects: UsableObjectConfig[];
  onMenuClose: () => void;
  updateActionParams: (newParams: WorkflowActionRemoveTagV1['params']) => void;
}

const ParamsRemoveTagV1: VFC<ParamsRemoveTagV1Props> = ({
  editable,
  errors,
  focusStep,
  params,
  usableObjects,
  onMenuClose,
  updateActionParams,
}) => {
  const possibleObjects = useMemo(() => taggableObjectsFor(usableObjects), [usableObjects]);

  const selectObject = (object: WorkflowActionRemoveTagV1['params']['object']) => {
    updateActionParams({
      ...params,
      object,
    });
  };

  const onChange = (tagIds: string[]) => {
    updateActionParams({
      ...params,
      'tag-ids': tagIds,
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionRemoveTagV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Object' htmlFor='params-remove-tag__object-input' />
      <ReactSelect
        value={params.object}
        options={possibleObjects}
        onChange={selectObject}
        id='params-remove-tag__object'
        disabled={!editable}
        hasError={hasErrorFor('object')}
        onFocusOption={focusStep}
        onMenuClose={onMenuClose}
      />
      {renderErrorMessageFor('object')}
      <Spacer height={2} />
      <ParamLabel labelText='Tag' htmlFor='params-remove-tag__tag-input' />
      <ConnectedTagSelect
        size='md'
        value={params['tag-ids']}
        tagScope='all'
        onChange={onChange}
        id='params-remove-tag__tag'
        hasError={hasErrorFor('tag-ids')}
        disabled={!editable}
      />
      {renderErrorMessageFor('tag-ids')}
    </>
  );
};

export default ParamsRemoveTagV1;
